import {fetchCAPI} from './fetch-capi'
import type {AhpReplicationFrame} from './replay-ahp-events'

/** Media type that negotiates AHP events (persisted ReplicationFrames) over SDK events. */
export const AHP_EVENTS_MEDIA_TYPE = 'application/vnd.github.ahp+json'

/** React Query key for a session's persisted AHP events. */
export function generateAhpEventsQueryKey(sessionId: string | undefined): string[] {
  return ['agent-session-ahp-events', sessionId ?? '']
}

/** Abstraction over the network call for testability. */
export type AhpEventsFetcher = (path: string) => Promise<Response>

const defaultFetcher: AhpEventsFetcher = (path: string) =>
  fetchCAPI({path, additionalHeaders: {Accept: AHP_EVENTS_MEDIA_TYPE}})

export interface AhpEventsResult {
  events: AhpReplicationFrame[]
  total: number
}

/**
 * Fetch all persisted AHP events for a session. Unlike SDK events, the AHP
 * response is not paginated — it returns the full ordered frame log.
 */
export async function fetchAhpSessionEvents(
  sessionId: string,
  fetcher: AhpEventsFetcher = defaultFetcher,
): Promise<AhpEventsResult> {
  const response = await fetcher(`/agents/sessions/${sessionId}/events`)
  if (!response.ok) {
    throw new Error(`Failed to fetch AHP session events: ${response.status} ${response.statusText}`)
  }
  const body = (await response.json()) as {events: AhpReplicationFrame[]; total: number}
  return {events: body.events ?? [], total: body.total ?? 0}
}
